/**
 * Which zone a page shows its times in.
 *
 * VentaHub had one zone per person, picked in the header, and showed every
 * page through it — wrong for a seller on several marketplaces, where a
 * German store's ads hours mean Berlin time and the person's own ticket
 * deadlines mean their own. Here a page resolves a zone from the most
 * specific thing said about it down to the least:
 *
 *   1. the person's choice for this page on this store
 *   2. the person's choice for this page, any store
 *   3. the store's own zone, when the page shows one — its ads profile's on
 *      an ads page, else its settings override or its marketplace's: a
 *      store's warehouse, orders and ads read in that store's local time
 *   4. the person's profile default (DV-Auth, on the session)
 *   5. the browser's zone
 *
 * A zone is always the person's own and always said of a page — there is no
 * app-wide, module-wide or store-wide row. Steps 1–2 are rows in
 * `/v1/me/timezones`, set on the time zones page the header opens; the page
 * keys come from the sidebar
 * (`components/layout/timezoneMap.ts`). Pure, so the chain is tested as a table. `useResolvedTimezone`
 * gathers the inputs and `useZone()` hands a component the answer.
 */
import type { ParseKeys } from 'i18next'

import type { StoreZoneRead, TimezoneOverrideRead } from '@/client'

import { TIMEZONE_COUNTRIES } from './timezoneCountries'

/** Where a resolved zone came from, for the chip to explain. */
export type TimezoneSource =
  'store-page' | 'page' | 'profile' | 'store-zone' | 'ads-zone' | 'browser'

/** Which of a store's zones a page wants when nobody has said otherwise. */
export type StoreZoneKind = 'store' | 'ads'

export type ResolvedTimezone = { zone: string; source: TimezoneSource }

export type ResolveInput = {
  /** The page's key, e.g. `marketing.ams`; null off the sidebar's map. */
  page: string | null
  /** The store the page is showing, when it shows one. */
  storeId?: number | undefined
  /** `ads` reads the ads profile's zone before the store's own. */
  kind?: StoreZoneKind
  /** The person's narrowings, from `/v1/me/timezones`. */
  overrides: readonly TimezoneOverrideRead[]
  /** The person's profile default, from DV-Auth; null or undefined for none. */
  profile?: string | null | undefined
  /** The store's zones, from `/v1/me/timezones`, when the page shows a store. */
  store?: StoreZoneRead | undefined
  /** The browser's zone; injectable for tests. */
  browser?: string
}

const validity = new Map<string, boolean>()

/** Whether the browser can format in this zone. Cached: `Intl` construction is not free. */
export function isTimezone(value: string | null | undefined): value is string {
  if (!value) return false
  let known = validity.get(value)
  if (known === undefined) {
    try {
      new Intl.DateTimeFormat('en', { timeZone: value })
      known = true
    } catch {
      known = false
    }
    validity.set(value, known)
  }
  return known
}

/** The browser's own zone, or UTC where it cannot say. */
export function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
}

/** Every IANA zone the browser knows, for a picker. */
export function timezoneNames(): string[] {
  return typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : []
}

export type TimezoneOption = { value: string; label: string; hint: string; icon?: string }

let options: TimezoneOption[] | undefined

/**
 * Every zone as a picker row — `America/New York` beside `GMT-5` — built once:
 * four hundred `Intl` formatters are not something to redo on every open.
 * A zone this browser lacks (a saved `Asia/Kolkata` on an old ICU) is added
 * with `extra`, so a saved choice still shows as chosen.
 */
export function timezoneOptions(extra?: string | null): TimezoneOption[] {
  options ??= timezoneNames().map(toOption)
  if (!extra || options.some((option) => option.value === extra)) return options
  return [toOption(extra), ...options]
}

function toOption(zone: string): TimezoneOption {
  const flag = timezoneFlag(zone)
  return {
    value: zone,
    label: zone.replace(/_/g, ' '),
    hint: timezoneOffsetLabel(zone),
    ...(flag ? { icon: flag } : {}),
  }
}

/**
 * The flag of the country a zone is in — 🇩🇪 for `Europe/Berlin` — or
 * undefined for a zone in none (`UTC`). Built from the two regional-indicator
 * letters, so it needs no images; a system without flag glyphs shows "DE".
 */
export function timezoneFlag(zone: string | null | undefined): string | undefined {
  const country = zone ? TIMEZONE_COUNTRIES[zone] : undefined
  if (!country) return undefined
  return String.fromCodePoint(...[...country].map((letter) => 0x1f1a5 + letter.charCodeAt(0)))
}

/** `GMT+05:30` for a zone at an instant, or an empty string for a zone the browser lacks. */
export function timezoneOffsetLabel(zone: string, at: Date = new Date()): string {
  try {
    const parts = new Intl.DateTimeFormat('en', { timeZone: zone, timeZoneName: 'shortOffset' })
    return parts.formatToParts(at).find((part) => part.type === 'timeZoneName')?.value ?? ''
  } catch {
    return ''
  }
}

function pick(
  overrides: readonly TimezoneOverrideRead[],
  storeId: number | null,
  page: string | null,
): string | undefined {
  const match = overrideFor(overrides, storeId, page)
  return isTimezone(match?.timezone) ? match.timezone : undefined
}

/**
 * What a level would read in without its own row: the chain with that one
 * row taken out. The settings page shows it as the "no choice" option, so the person
 * sees what clearing a row will give them before they clear it.
 */
export function fallbackFor(
  input: ResolveInput,
  level: { storeId: number | null; page: string | null },
): ResolvedTimezone {
  return resolveTimezone({
    ...input,
    overrides: input.overrides.filter(
      (each) => !(each.store_id === level.storeId && each.page === level.page),
    ),
  })
}

/** The person's own row for exactly this store and page, if they set one. */
export function overrideFor(
  overrides: readonly TimezoneOverrideRead[],
  storeId: number | null,
  page: string | null,
): TimezoneOverrideRead | undefined {
  return overrides.find((each) => each.store_id === storeId && each.page === page)
}

/** Walk the chain in the module comment and say which step answered. */
export function resolveTimezone(input: ResolveInput): ResolvedTimezone {
  const { page, overrides, store } = input
  const storeId = input.storeId ?? null
  if (storeId !== null && page !== null) {
    const both = pick(overrides, storeId, page)
    if (both) return { zone: both, source: 'store-page' }
  }
  if (page !== null) {
    const forPage = pick(overrides, null, page)
    if (forPage) return { zone: forPage, source: 'page' }
  }
  if (store && storeId !== null) {
    if (input.kind === 'ads' && isTimezone(store.ads_timezone)) {
      return { zone: store.ads_timezone, source: 'ads-zone' }
    }
    if (isTimezone(store.timezone)) return { zone: store.timezone, source: 'store-zone' }
  }
  if (isTimezone(input.profile)) return { zone: input.profile, source: 'profile' }
  const browser = input.browser ?? browserTimezone()
  return { zone: isTimezone(browser) ? browser : 'UTC', source: 'browser' }
}

/** The copy that says where a page's zone came from, for the header and the settings page. */
export const TIMEZONE_SOURCE_LABELS = {
  'store-page': 'common.timezone.source.storePage',
  page: 'common.timezone.source.page',
  profile: 'common.timezone.source.profile',
  'store-zone': 'common.timezone.source.storeZone',
  'ads-zone': 'common.timezone.source.adsZone',
  browser: 'common.timezone.source.browser',
} as const satisfies Record<TimezoneSource, ParseKeys>
